import { AfterViewInit, Component, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { OrderSummaryRow } from '@common/order-summary-row';
import { CardComponent } from '@components/card/card.component';
import { EmbeddedOrderSummaryComponent } from '@components/embedded-order-summary/embedded-order-summary.component';
import { PaymentStepTabsComponent } from '@components/payment-step-tabs/payment-step-tabs.component';
import { GiftCardPayment, Payment } from '@medology/ngx-payment';
import { OrderUpsell } from '@models/order-upsell';
import { DocumentService } from '@services/document.service';

@Component({
  selector: 'app-payment-card-form',
  templateUrl: './payment-card-form.component.html',
  styleUrls: ['./payment-card-form.component.scss'],
  imports: [CardComponent, EmbeddedOrderSummaryComponent, PaymentStepTabsComponent],
})
export class PaymentCardFormComponent implements AfterViewInit {
  @Input() public lineItems: OrderUpsell[] = [];
  @Input() public previousBalanceInCents: number = 0;
  @Input() public enablePayLater: boolean = true;

  @Output() public paymentsSelected = new EventEmitter<Payment[]>();
  @Output() public formValid = new EventEmitter<void>();
  @ViewChild('paymentStepTabs', { static: false }) public paymentStepTabsComponent: PaymentStepTabsComponent;

  private documentService = inject(DocumentService);
  private payments: Payment[] = [];

  /**
   * Gets whether the pay later tab is selected from the payment step tabs component.
   */
  public get isPayLater(): boolean {
    return !this.paymentStepTabsComponent?.isPayNowTabSelected;
  }

  /**
   * Gets the total amount in cents before gift card is applied.
   *
   * @returns {number} the total amount in cents
   */
  public get total(): number {
    const upsellTotal = this.lineItems.reduce((acc, upsell) => acc + (upsell.upsell_price || upsell.price), 0);

    return upsellTotal + this.previousBalanceInCents;
  }

  /**
   * Gets the due amount in cents after applying the gift card amount.
   *
   * @returns {number} the due amount in cents
   */
  public get totalDue(): number {
    return this.total - this.giftCardAmountInCents;
  }

  /**
   * Gets the order summary rows based on the line items, previous balance, and gift card.
   *
   * @returns {OrderSummaryRow[]} the order summary rows
   */
  public get orderSummaryRows(): OrderSummaryRow[] {
    return [...this.createPreviousBalanceRow(), ...this.createLineItemRows(), ...this.createGiftCardRow()];
  }

  /**
   * Gets the gift card amount in cents, capped at the total before gift card.
   *
   * @returns {number} the gift card amount in cents
   */
  private get giftCardAmountInCents(): number {
    const giftCard = this.getGiftCardPayment();
    if (!giftCard) {
      return 0;
    }

    return Math.min(giftCard.balance * 100, this.total);
  }

  /**
   * Scrolls to bottom after the view is initialized.
   */
  public ngAfterViewInit(): void {
    this.documentService.scrollToBottom();
  }

  /**
   * Handles payment selection from the payment form and emits the selected payments.
   *
   * @param {Payment[]} payments the selected payments
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.paymentsSelected.emit(payments);
  }

  /**
   * Gets the validation error message for the order form submission.
   *
   * @returns {boolean} true if the forms are valid and we have payments or is pay later selected
   */
  public validateForm(): boolean {
    if (this.isPayLater) {
      return true;
    }

    return this.paymentStepTabsComponent?.validatePaymentForm() && this.payments.length > 0;
  }

  /**
   * Gets the gift card payment from the selected payments.
   *
   * @returns {GiftCardPayment | undefined} the gift card payment, or undefined if none
   */
  private getGiftCardPayment(): GiftCardPayment | undefined {
    return this.payments.find((p): p is GiftCardPayment => p instanceof GiftCardPayment);
  }

  /**
   * Creates the previous balance row for the order summary.
   *
   * @returns {OrderSummaryRow[]} the previous balance row, or empty array if no balance
   */
  private createPreviousBalanceRow(): OrderSummaryRow[] {
    if (this.previousBalanceInCents <= 0) {
      return [];
    }

    return [
      {
        item: 'Previous Balance',
        amountInCents: this.previousBalanceInCents,
        shouldDisplayRow: true,
        dataQa: 'previous-balance-row',
      },
    ];
  }

  /**
   * Creates the line item rows for the order summary.
   *
   * @returns {OrderSummaryRow[]} the line item rows, or empty array if no items
   */
  private createLineItemRows(): OrderSummaryRow[] {
    if (!this.lineItems || !this.lineItems.length) {
      return [];
    }

    return this.lineItems.map((upsellItem) => ({
      item: upsellItem.name,
      amountInCents: upsellItem.upsell_price || upsellItem.price,
      shouldDisplayRow: true,
    }));
  }

  /**
   * Creates the gift card discount row for the order summary.
   *
   * @returns {OrderSummaryRow[]} the gift card row, or empty array if no gift card applied
   */
  private createGiftCardRow(): OrderSummaryRow[] {
    if (this.giftCardAmountInCents <= 0) {
      return [];
    }

    return [
      {
        item: 'Gift Card',
        amountInCents: -this.giftCardAmountInCents,
        shouldDisplayRow: true,
        dataQa: 'gift-card-row',
      },
    ];
  }
}
